import { assertNever } from '@eversports/utility-types'
import Activity from './Activity'
import Availability from './Availability'
import Image from './Image'
import Price, { PartialPriceInformation } from './Price'
import Sport from './Sport'
import Trainer from './Trainer'
import Venue from './Venue'
import Location from './Location'

export interface UsageLimit {
  usageLimit: number
  type: UsageLimitType
  unit: UsageLimitUnit
  currency: string | null
  repeating: Repeating | null
}

export enum UsageLimitType {
  USAGE_LIMIT_TYPE_TOTAL = 'USAGE_LIMIT_TYPE_TOTAL',
  USAGE_LIMIT_TYPE_REPEATING = 'USAGE_LIMIT_TYPE_REPEATING',
  USAGE_LIMIT_TYPE_PER_BOOKING = 'USAGE_LIMIT_TYPE_PER_BOOKING',
}

enum UsageLimitUnit {
  USAGE_LIMIT_UNIT_HOURS = 'USAGE_LIMIT_UNIT_HOURS',
  USAGE_LIMIT_UNIT_MONETARY = 'USAGE_LIMIT_UNIT_MONETARY',
  USAGE_LIMIT_UNIT_UNITS = 'USAGE_LIMIT_UNIT_UNITS',
}

interface Repeating {
  interval: Interval
  isTransferable: boolean
}

export interface Interval {
  type: IntervalUnit
  value: number
}

enum IntervalUnit {
  INTERVAL_UNIT_HOURS = 'INTERVAL_UNIT_HOURS',
  INTERVAL_UNIT_DAYS = 'INTERVAL_UNIT_DAYS',
  INTERVAL_UNIT_WEEKS = 'INTERVAL_UNIT_WEEKS',
  INTERVAL_UNIT_MONTHS = 'INTERVAL_UNIT_MONTHS',
  INTERVAL_UNIT_YEARS = 'INTERVAL_UNIT_YEARS',
}

export interface Validity {
  startType: ValidityStartType
  startDate: string | null
  duration: Interval
}

enum ValidityStartType {
  VALIDITY_START_TYPE_PURCHASE_DATE = 'VALIDITY_START_TYPE_PURCHASE_DATE',
  VALIDITY_START_TYPE_FIRST_BOOKING = 'VALIDITY_START_TYPE_FIRST_BOOKING',
  VALIDITY_START_TYPE_DATE = 'VALIDITY_START_TYPE_DATE',
}

interface Runtime {
  initial: Interval
  renewal: Renewal | null
  pausable: boolean
}

interface Renewal {
  interval: Interval
  cancellation: Interval
}

export interface Promotion {
  price: Price
  period: Interval
}

interface PaymentPlan {
  interval: Interval
  offset: number | null
  recurring: Price
  initial: Price | null
  promotion: Promotion | null
}

export enum CardType {
  CARD_TYPE_EVENT_BLOCK = 'CARD_TYPE_EVENT_BLOCK',
  CARD_TYPE_EVENT_TIME = 'CARD_TYPE_EVENT_TIME',
  CARD_TYPE_COURT_BLOCK = 'CARD_TYPE_COURT_BLOCK',
  CARD_TYPE_COURT_ABO = 'CARD_TYPE_COURT_ABO',
  CARD_TYPE_COURT_SEASON = 'CARD_TYPE_COURT_SEASON',
  CARD_TYPE_COURT_CREDIT = 'CARD_TYPE_COURT_CREDIT',
}

interface ProductPurchaseInformation {
  purchasable: boolean
  additionalInformation: string | null
}

interface Card {
  __typename: 'Card'
  cardType: CardType
  id: string
  name: string
  price: Price
  originalPrice: Price | null
  usages: Array<UsageLimit>
  description: string | null
  validity: Validity | null
  venues: Array<Venue>
  activities: Array<Activity>
  sports: Array<Sport>
  availabilities: Array<Availability>
  productPurchaseInformation?: ProductPurchaseInformation
  isTrialCard: boolean
  isCorporatePrice: boolean
}

export interface Membership {
  __typename: 'Membership'
  id: string
  name: string
  description: string | null
  conditions: string | null
  paymentAndCancellationTerms: string | null
  activities: Array<Activity>
  usages: Array<UsageLimit>
  runtime: Runtime
  paymentPlan: PaymentPlan
  partialPriceInformation: PartialPriceInformation | null
  venues: Array<Venue>
}

interface Ticket {
  __typename: 'Ticket'
  id: string
  name: string
  standardPrice: Price
  earlyBirdPrice: Price | null
  lastMinutePrice: Price | null
  currentPriceType: TicketPriceType
  earlyBirdUntil: string | null
  lastMinuteFrom: string | null
  validForSessions: Array<EventSession>
  productPurchaseInformation: ProductPurchaseInformation
}

export enum TicketPriceType {
  TICKET_PRICE_TYPE_EARLY_BIRD = 'TICKET_PRICE_TYPE_EARLY_BIRD',
  TICKET_PRICE_TYPE_STANDARD = 'TICKET_PRICE_TYPE_STANDARD',
  TICKET_PRICE_TYPE_LAST_MINUTE = 'TICKET_PRICE_TYPE_LAST_MINUTE',
}

interface EventSession {
  id?: string
  start: string
  end: string
  name: string | null
  image?: Image | null
  trainer?: Trainer | null
  location: Location | null
}

interface SinglePurchase {
  __typename: 'SinglePurchase'
  id: string
  name: string
  price: Price
}

interface Voucher {
  __typename: 'Voucher'
  id: string
  name: string
  value: Price
  conditions: string | null
  validityInterval: Interval | null
  image: Image | null
}

interface Recording {
  __typename: 'Recording'
  id: string
  price: Price | null
  availableUntil: string | null
  description: string | null
  name: string
  recordingStartDate: string | null
  recordingEndDate: string | null
  trainer: Trainer | null
  image: Image | null
}

export interface UsageLimitPeriod {
  start: string
  end: string
  allowedBookings: number
  usedBookings: number
}

interface AssignedProductBase<T, P> {
  id: string
  __typename: T
  product: P
  assignedProductValidity: {
    isValid: boolean
    additionalInformation: string | null
    usageLimitPeriod: UsageLimitPeriod | null
  }
}

type AssignedMembership = AssignedProductBase<'AssignedMembership', Membership>

interface AssignedCard extends AssignedProductBase<'AssignedCard', Card> {
  validUntil?: string | null
  usagesLeft?: number | null
  totalUsages?: number | null
}

export interface AssignedRecording {
  __typename: 'AssignedRecording'
  id: string
  product: Recording
  validUntil?: string
  url: string
}

export interface GenericAssignedProduct {
  __typename: 'GenericAssignedProduct'
  id: string
}

export type SettleWith = PurchaseOption | ConfirmWithOption | GenericAssignedProduct

export interface ProductSelectionInitialValues {
  settleWithId: string
}

export const isConfirmWithOption = (product: SettleWith): product is ConfirmWithOption =>
  product.__typename === 'AssignedCard' || product.__typename === 'AssignedMembership'

export const isPurchaseOption = (product: SettleWith): product is PurchaseOption =>
  product.__typename === 'Card' ||
  product.__typename === 'Membership' ||
  product.__typename === 'Ticket' ||
  product.__typename === 'SinglePurchase'

export const isPurchaseOptionAService = (product: PurchaseOption): product is Card | SinglePurchase =>
  product.__typename === 'Card' || product.__typename === 'SinglePurchase'

export const isPurchaseOptionATicket = (product: PurchaseOption): product is Ticket => product.__typename === 'Ticket'

export const isPurchaseOptionAMembership = (product: PurchaseOption): product is Membership =>
  product.__typename === 'Membership'

export const getCurrentTicketPrice = (ticket: Ticket): Price => {
  switch (ticket.currentPriceType) {
    case TicketPriceType.TICKET_PRICE_TYPE_EARLY_BIRD:
      return ticket.earlyBirdPrice!
    case TicketPriceType.TICKET_PRICE_TYPE_STANDARD:
      return ticket.standardPrice
    case TicketPriceType.TICKET_PRICE_TYPE_LAST_MINUTE:
      return ticket.lastMinutePrice!
    default:
      return assertNever(ticket.currentPriceType)
  }
}

export const isPurchaseOptionPurchasable = (product: PurchaseOption) => {
  if (product.__typename !== 'Card' && product.__typename !== 'Ticket') {
    return true
  }

  return product.productPurchaseInformation ? product.productPurchaseInformation.purchasable : true
}

export type PurchaseOption = Card | Membership | Ticket | SinglePurchase
export type ConfirmWithOption = AssignedCard | AssignedMembership
export type ShopProduct = Card | Membership | Voucher | Recording
export type AssignedProduct = AssignedCard | AssignedMembership | AssignedRecording
